import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Badge } from './ui/badge';
import { Star, RefreshCw, AlertTriangle } from 'lucide-react';

/**
 * Recent Feedback Scores — small dashboard section for Admin / Front-Desk.
 * Powers the internal-rating gate that precedes the Google review ask.
 *
 * Score buckets (5 / 4 / 3-or-less) with rupee-value colour coding:
 *   5 → emerald, 4 → sky, 3- → red
 */
const scoreStyle = (s) => {
  if (s === 5) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  return 'bg-red-100 text-red-800 border-red-200';
};

const scoreLabel = (s) => {
  if (s === 5) return '5';
  return '4 or less';
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_e) {
    return iso;
  }
};

const RecentFeedbackScoresSection = ({ user }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, '5': 0, '4': 0, '3_or_less': 0 });
  const [loading, setLoading] = useState(false);

  const canView = user?.role === 'Admin';

  const load = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const res = await api.get('/admin/feedback-scores?limit=15');
      setRows(res.data?.rows || []);
      setSummary(res.data?.summary || { total: 0, '5': 0, '4': 0, '3_or_less': 0 });
    } catch (_e) {
      // Silent — dashboard section, don't spam toasts on refresh cycles
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canView) return null;

  return (
    <div className="mt-6" data-testid="dashboard-feedback-scores">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-500" />
          Recent Feedback Scores
          <Badge variant="outline" className="text-xs">{summary.total}</Badge>
        </h2>
        <button
          onClick={load}
          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
          data-testid="dashboard-feedback-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="feedback-tile-5">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">5-star</div>
          <div className="text-2xl font-bold text-emerald-900 mt-1">{summary['5']}</div>
        </div>
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-3" data-testid="feedback-tile-4">
          <div className="text-xs uppercase tracking-wide text-sky-700 font-semibold">4-star</div>
          <div className="text-2xl font-bold text-sky-900 mt-1">{summary['4']}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-3" data-testid="feedback-tile-low">
          <div className="text-xs uppercase tracking-wide text-red-700 font-semibold flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> 3 or less
          </div>
          <div className="text-2xl font-bold text-red-900 mt-1">{summary['3_or_less']}</div>
        </div>
      </div>

      {/* Recent list */}
      {rows.length === 0 ? (
        <div className="text-sm text-gray-500 bg-gray-50 rounded-xl p-4 text-center border border-dashed border-gray-200">
          No feedback ratings yet. Ratings arrive via the WhatsApp feedback ask.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
          {rows.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-3 p-3 hover:bg-gray-50"
              data-testid={`feedback-row-${r.id}`}
            >
              <Badge className={`text-xs font-semibold ${scoreStyle(Number(r.feedback_score))}`}>
                {scoreLabel(Number(r.feedback_score))}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {r.patient_name || '—'}{' '}
                  <span className="text-xs text-gray-500 font-normal">{r.patient_phone || ''}</span>
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {r.source_service_name || '—'}
                  {r.source_appointment_ref && <span> · {r.source_appointment_ref}</span>}
                </div>
              </div>
              <div className="text-xs text-gray-400 whitespace-nowrap">
                {fmtWhen(r.feedback_response_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentFeedbackScoresSection;
